import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { AxiosError } from 'axios';
import { calculateClientPreview } from '@/lib/api/clients';
import { CalculationResult } from '@/types/calculations';
import { useDebounce } from '@/hooks/useDebounce';
import { buildCreateClientPayload } from '../utils';
import { FormData } from '../types';
import { isIomFormulaSelection } from '../formula-utils';
import { calculateAgeFromDateString } from '../date-utils';

interface UseCalculationParams {
  currentStep: number;
  formData: FormData;
  applyGrowthFactor?: boolean;
}

interface CalculationValidationWarning {
  field?: string;
  message?: string;
  minimumRequired?: number;
  actualValue?: number;
  canOverride?: boolean;
  warningType?: string;
}

const isActivityLevelRequired = (age: number, formulaId: string): boolean => {
  if (age < 1) return false;
  if (isIomFormulaSelection(formulaId)) {
    return age >= 3;
  }
  return true;
};

const isAxiosCanceledError = (error: unknown): boolean => {
  const axiosError = error as AxiosError;
  return axiosError?.code === 'ERR_CANCELED';
};

const getResponseData = (error: unknown): Record<string, unknown> | null => {
  const axiosError = error as AxiosError;
  const responseData = axiosError?.response?.data;
  if (responseData && typeof responseData === 'object') {
    return responseData as Record<string, unknown>;
  }
  return null;
};

const extractValidationWarnings = (error: unknown): CalculationValidationWarning[] => {
  const responseData = getResponseData(error);
  const maybeWarnings = responseData?.validationWarnings;
  if (Array.isArray(maybeWarnings)) {
    return maybeWarnings as CalculationValidationWarning[];
  }
  return [];
};

const extractValidationDetailsMessage = (error: unknown): string | null => {
  const responseData = getResponseData(error);
  const details = responseData?.details;

  if (Array.isArray(details) && details.length > 0) {
    const firstError = details[0] as Record<string, unknown>;
    const field = typeof firstError?.field === 'string' ? firstError.field : null;
    const message = typeof firstError?.message === 'string' ? firstError.message : null;

    if (field && message) {
      return `${field}: ${message}`;
    }
    if (message) {
      return message;
    }
  }

  if (typeof responseData?.error === 'string' && responseData.error.trim()) {
    return responseData.error;
  }

  if (typeof responseData?.message === 'string' && responseData.message.trim()) {
    return responseData.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return null;
};

const normalizePreview = (preview: CalculationResult): CalculationResult => ({
  ...preview,
  eerWithoutGrowthFactor: preview.eerWithoutGrowthFactor ?? null,
});

export const useCalculation = ({ currentStep, formData, applyGrowthFactor = true }: UseCalculationParams) => {
  const [calculationResult, setCalculationResult] = useState<CalculationResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState<string | null>(null);
  const [validationWarnings, setValidationWarnings] = useState<CalculationValidationWarning[]>([]);
  const latestRequestIdRef = useRef(0);

  const previewPayload = useMemo(() => {
    if (currentStep !== 2) return null;

    const requiredTextFields = [
      formData.email,
      formData.phone_country_code,
      formData.firstName,
      formData.lastName,
      formData.dateOfBirth,
      formData.gender,
      formData.formulaId,
    ];

    const hasRequiredTextFields = requiredTextFields.every((value) => typeof value === 'string' && value.trim().length > 0);
    if (!hasRequiredTextFields) return null;

    const weight = parseFloat(formData.weightKg);
    const height = parseFloat(formData.heightCm);
    if (!Number.isFinite(weight) || weight <= 0 || !Number.isFinite(height) || height <= 0) {
      return null;
    }

    const age = calculateAgeFromDateString(formData.dateOfBirth) ?? 0;
    if (isActivityLevelRequired(age, formData.formulaId || '') && !formData.activityLevel) {
      return null;
    }

    return buildCreateClientPayload(formData, { applyGrowthFactor });
  }, [currentStep, formData, applyGrowthFactor]);

  const debouncedPreviewPayload = useDebounce(previewPayload, 700);

  useEffect(() => {
    if (!debouncedPreviewPayload) {
      setIsCalculating(false);
      setCalculationResult(null);
      setCalculationError(null);
      setValidationWarnings([]);
      return;
    }

    const requestId = ++latestRequestIdRef.current;
    const controller = new AbortController();

    setIsCalculating(true);
    setCalculationError(null);
    setValidationWarnings([]);

    const fetchPreview = async () => {
      try {
        const response = await calculateClientPreview(debouncedPreviewPayload, controller.signal);
        if (requestId !== latestRequestIdRef.current) return;

        setCalculationResult(normalizePreview(response.preview));
      } catch (error: unknown) {
        if (controller.signal.aborted || isAxiosCanceledError(error)) {
          return;
        }
        if (requestId !== latestRequestIdRef.current) return;

        const warnings = extractValidationWarnings(error);
        if (warnings.length > 0) {
          setValidationWarnings(warnings);
          setCalculationResult(null);
          return;
        }

        setCalculationResult(null);
        setCalculationError(
          extractValidationDetailsMessage(error) || 'Unable to calculate preview with current inputs.'
        );
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setIsCalculating(false);
        }
      }
    };

    fetchPreview();

    return () => {
      controller.abort();
    };
  }, [debouncedPreviewPayload]);

  const reset = useCallback(() => {
    latestRequestIdRef.current += 1;
    setCalculationResult(null);
    setCalculationError(null);
    setValidationWarnings([]);
    setIsCalculating(false);
  }, []);

  return {
    calculationResult,
    isCalculating,
    calculationError,
    validationWarnings,
    reset,
  };
};
